import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  message,
  Radio,
  Button,
  Col,
  Row,
  Spin,
} from "antd";
import { ButtonDelete } from "../common/Buttons";
import {
  IconOrdersAlertsBack,
  IconOrdersAlertsSettings,
} from "../Layout/Icons";
import { MdEdit, MdAccessTime } from "react-icons/md";
import styled from "styled-components";
import axios from "axios";
import { FixedSizeList as List } from "react-window";
import { useSelector } from "react-redux";

const OrdersAlertsModal = ({
  visible,
  onCancel,
  alerts,
  refreshAlerts,
  onEdit,
}) => {
  const [form] = Form.useForm();
  const [modalType, setModalType] = React.useState("orders");
  const [rulesData, setRulesData] = React.useState([]);
  const [selectedValue, setSelectedValue] = React.useState("customer_service");
  const [selectedAlert, setSelectedAlert] = React.useState(null);
  const [alertsList, setAlertsList] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [movedOrderIds, setMovedOrderIds] = React.useState([]);
  const userRoleTitle = useSelector((state) => state.auth.user.user_role.title);
  const days = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} day${i + 1 === 1 ? "" : "s"}`,
  }));

  const times = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i} hour${i === 1 ? "" : "s"}`,
  }));

  const editAlert = async (values) => {
    try {
      await axios.put(`/api/v1/alerts/${selectedAlert.id}`, {
        alert: {
          rule: values.alertRule,
          days: values.triggerAlertDay,
          hours: values.triggerAlertTime,
          role: selectedValue,
        },
      });
      message.success("Alert updated successfully");
      form.resetFields();
      setSelectedAlert(null);
      setModalType("manage");
      fetchAlerts();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update alert");
    }
  };

  const dismissOrderAlert = async (id) => {
    try {
      setRefreshing(true);
      await axios.delete(`/api/v1/order_alerts/${id}`);
      message.success("Alert dismissed successfully");
      await refreshAlerts();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to dismiss alert"
      );
    } finally {
      setRefreshing(false);
    }
  };

  const editOrderAlert = async (values) => {
    try {
      setRefreshing(true);
      await axios.put(`/api/v1/order_alerts/${values}`, {
        order_alert: {
          seen_at: Date.now(),
        },
      });
      message.success("Alert updated successfully");
      form.resetFields();
      // await refreshAlerts();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update alert");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`/api/v1/alerts/${id}`);
      message.success("Alert deleted successfully");
      fetchAlerts();
    } catch (error) {
      console.error("Error deleting alert:", error);
      message.error(error?.response?.data?.message || "Failed to delete alert");
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get("/api/v1/alerts");
      setAlertsList(response.data.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      message.error("Failed to fetch alerts");
    }
  };

  const createAlert = async (values) => {
    try {
      await axios.post("/api/v1/alerts", {
        alert: {
          rule: values.alertRule,
          days: values.triggerAlertDay,
          hours: values.triggerAlertTime,
          role: selectedValue,
        },
      });
      message.success("Alert created successfully");
      form.resetFields();
      setModalType("manage");
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to create alert");
    }
  };
  const fetchRules = async () => {
    try {
      const response = await axios.get("/api/v1/alerts/rules");
      const rulesData = response.data.data;

      const ruleOptions = Object.entries(rulesData).map(([key, value]) => ({
        value: key,
        label: value.title,
      }));

      setRulesData(ruleOptions);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [modalType]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (selectedAlert) {
          editAlert(values);
        } else {
          createAlert(values);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEdit = (alert) => {
    setSelectedAlert(alert);
    form.setFieldsValue({
      alertRule: alert.rule,
      triggerAlertDay: alert.days,
      triggerAlertTime: alert.hours,
    });
    setSelectedValue(alert.role);
    setModalType("edit");
  };

  const renderGroups = () => {
    const enrichedAlerts = alerts.map((orderAlert) => {
      const alert = alertsList.find((a) => a.id === orderAlert.alert_id);
      return {
        ...orderAlert,
        rule: alert?.rule,
      };
    });

    const groupedAlerts = enrichedAlerts.reduce((acc, alert) => {
      if (!acc[alert.rule]) {
        acc[alert.rule] = [];
      }
      acc[alert.rule].push(alert);
      return acc;
    }, {});

    const groupedAlertsEntries = Object.entries(groupedAlerts);

    if (groupedAlertsEntries.length === 0) {
      return <div className="empty-list-container">
        <p style={{ margin: 0 }}>List is empty</p>
      </div>;
    }

    return groupedAlertsEntries.map(([rule, alerts]) => {
      const ruleTitle = rulesData.find((r) => r.value === rule)?.label || rule;
      return (
        <div
          key={rule}
          className={`OrdersAlertsModal-groupItem ${selectedGroup === rule ? "OrdersAlertsModal-groupItem-active" : ""}`}
          onClick={() => setSelectedGroup(rule)}
          style={{ cursor: "pointer" }}
        >
          <p className="OrdersAlertsModal-groupItemText">{ruleTitle}</p>
          <div className="OrdersAlertsModal-groupItemBadge">
            <p className="OrdersAlertsModal-groupItemBadgeText">
              {alerts.length}
            </p>
          </div>
        </div>
      );
    });
  };

  const filteredAlerts = React.useMemo(() => {
    const sortedAlerts = [...alerts].sort((a, b) => {
      if (movedOrderIds.includes(a.id) && !movedOrderIds.includes(b.id)) {
        return 1;
      }
      if (!movedOrderIds.includes(a.id) && movedOrderIds.includes(b.id)) {
        return -1;
      }
      return 0;
    });

    if (!selectedGroup) return sortedAlerts;

    return sortedAlerts.filter((orderAlert) => {
      const alert = alertsList.find((a) => a.id === orderAlert.alert_id);
      return alert?.rule === selectedGroup;
    });
  }, [alerts, alertsList, selectedGroup, movedOrderIds]);

  const AlertRow = ({ index, style, data }) => {
    const alert = data[index];
    const isMoved = movedOrderIds.includes(alert.id);
    const seenAtDate = new Date().toISOString();

    return (
      <div style={style}>
        <div
          key={alert.id}
          className={
            alert.seen_at || isMoved
              ? "OrdersAlertsModal-order"
              : "OrdersAlertsModal-order-active"
          }
          onClick={() => {
            if (!alert.seen_at) {
              editOrderAlert(alert.id);
              setMovedOrderIds((prev) => [...prev, alert.id]);
            }
          }}
        >
          <div className="OrdersAlertsModal-orderHeader">
            <div className="OrdersAlertsModal-orderHeaderTitle">
              <div
                className={
                  alert.seen_at || isMoved
                    ? "OrdersAlertsModal-orderHeaderTitleIcon"
                    : "OrdersAlertsModal-orderHeaderTitleIcon-active"
                }
              />
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  editOrderAlert(alert.id);
                  setMovedOrderIds((prev) => [...prev, alert.id]);
                  onEdit(null, alert.order_id);
                  onCancel();
                }}
                className="OrdersAlertsModal-orderHeaderTitleText"
              >
                {alert.title}
              </p>
            </div>
            <div>
              <p
                className="OrdersAlertsModal-orderHeaderDismiss"
              >
                Dismiss
              </p>
            </div>
          </div>
          <div className="OrdersAlertsModal-orderBody">
            <p className="OrdersAlertsModal-orderBodyText">{alert.title}</p>
          </div>
          <div>
            <p className="OrdersAlertsModal-orderFooter">
              {isMoved ? seenAtDate : new Date(alert.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const modalBody = () => {
    if (modalType === "orders") {
      return (
        <Modal
          title={
            <div className="OrdersAlertsModal-header">
              <p className="OrdersAlertsModal-headerTitle">Orders alerts</p>
              <div
                onClick={() => setModalType("manage")}
                className="cursor-pointer d-flex"
                style={{ marginLeft: 5 }}
              >
                <IconOrdersAlertsSettings />
              </div>
            </div>
          }
          visible={visible}
          onCancel={onCancel}
          footer={null}
          width={600}
          bodyStyle={{ padding: "14px 24px" }}
        >
          <div>
            <div>
              <p className="OrdersAlertsModal-title">Groups</p>
              <div className="OrdersAlertsModal-group">{renderGroups()}</div>
            </div>
            <div>
              <p className="OrdersAlertsModal-title">Orders</p>
              <div style={{ height: "50vh" }}>
                {refreshing ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spin size="large" />
                  </div>
                ) : (
                  filteredAlerts.length === 0 ? (
                    <div className="empty-list-container">
                      <p style={{ margin: 0 }}>List is empty</p>
                    </div>
                  ) : (
                    <List
                      height={window.innerHeight * 0.5}
                      itemCount={filteredAlerts.length}
                      itemSize={100}
                      width="100%"
                      itemData={filteredAlerts}
                    >
                      {AlertRow}
                    </List>
                  )
                )}
              </div>
            </div>
          </div>
        </Modal>
      );
    } else if (modalType === "manage") {
      return (
        <Modal
          title={
            <div className="OrdersAlertsModal-header">
              <div
                onClick={() => setModalType("orders")}
                className="cursor-pointer d-flex"
                style={{ marginRight: 5 }}
              >
                <IconOrdersAlertsBack />
              </div>
              <p className="OrdersAlertsModal-headerTitle">Manage alerts</p>
            </div>
          }
          visible={visible}
          onCancel={onCancel}
          width={600}
          footer={[
            <Button
              key="ok"
              type="primary"
              onClick={() => setModalType("edit")}
            >
              Create new
            </Button>,
          ]}
          bodyStyle={{ padding: "14px 24px" }}
        >
          <div>
            {alertsList.length === 0 ? (
              <div className="empty-list-container">
                <p style={{ margin: 0 }}>List is empty, create your first alert</p>
              </div>
            ) : (
              alertsList.map((alert) => (
                <div key={alert.id} className="order-alert-flex order-alert-item">
                  <p className="order-alert-text">
                    {rulesData.find((r) => r.value === alert.rule)?.label || alert.rule}
                  </p>
                  <div className="order-alert-flex-center">
                    <MdAccessTime className="mr-1" color="#929292" />
                    <p className="order-alert-text">
                      {alert.days > 0
                        ? `${alert.days} day${alert.days > 1 ? "s" : ""}, `
                        : ""}
                      {alert.hours} hour{alert.hours > 1 ? "s" : ""}
                    </p>
                    <div onClick={() => { userRoleTitle === "Super Admin" ? handleEdit(alert) : null }}>
                      <MdEdit className="order-alert-icon" color="#929292" />
                    </div>
                    <ButtonDelete disabled={userRoleTitle === "Super Admin" ? false : true} onDelete={() => { userRoleTitle === "Super Admin" ? deleteAlert(alert.id) : null }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal>
      );
    } else if (modalType === "edit") {
      return (
        <Modal
          title={
            <div className="OrdersAlertsModal-header">
              <div
                onClick={() => setModalType("manage")}
                className="cursor-pointer d-flex"
                style={{ marginRight: 5 }}
              >
                <IconOrdersAlertsBack />
              </div>
              <p className="OrdersAlertsModal-headerTitle">
                {selectedAlert ? "Edit alert" : "Create alert"}
              </p>
            </div>
          }
          visible={visible}
          onCancel={onCancel}
          width={600}
          footer={[
            <Button key="cancel" onClick={() => setModalType("manage")}>
              Back
            </Button>,
            <Button key="ok" type="primary" onClick={handleOk}>
              {selectedAlert ? "Update" : "Create"}
            </Button>,
          ]}
          bodyStyle={{ padding: "14px 24px" }}
        >
          <Form form={form} layout="vertical">
            <Container className="d-flex">
              <Form.Item
                name="alertRule"
                label="Alert rule"
                style={{ flex: 1, marginRight: 8 }}
                rules={[
                  { required: true, message: "Please select an alert rule" },
                ]}
              >
                <Select
                  showSearch
                  options={rulesData}
                  placeholder="Select alert rule"
                  style={{ maxWidth: 200 }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
              <Form.Item
                name="triggerAlertDay"
                label="Trigger Alert after"
                style={{ flex: 1, marginLeft: 8 }}
                rules={[{ required: false, message: "Please select days" }]}
              >
                <Select
                  showSearch
                  options={days}
                  placeholder="Select days"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
              <Form.Item
                name="triggerAlertTime"
                label="&nbsp;"
                style={{ flex: 1, marginLeft: 8 }}
                rules={[{ required: false, message: "Please select time" }]}
              >
                <Select
                  showSearch
                  options={times}
                  placeholder="Select time"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  optionFilterProp="label"
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
            </Container>
            <Col span={24}>
              <Form.Item
                name="assignAlertForRole"
                label="Assign alert for role"
                rules={[{ required: false, message: "Please select a role" }]}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <div
                      className="card-type ant-input cursor-pointer"
                      style={{
                        marginBottom: 16,
                        height: 32,
                        borderColor:
                          selectedValue === "customer_service" ? "#03B0F7" : "",
                        backgroundColor:
                          selectedValue === "customer_service" ? "#EDF9FE" : "",
                      }}
                      onClick={() => setSelectedValue("customer_service")}
                    >
                      <Radio
                        className={
                          selectedValue === "customer_service"
                            ? "selected-radio"
                            : ""
                        }
                        style={{
                          color:
                            selectedValue === "customer_service"
                              ? "#03B0F7"
                              : "",
                        }}
                        checked={selectedValue === "customer_service"}
                        value="customer_service"
                      >
                        Customer service
                      </Radio>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div
                      className="card-type ant-input cursor-pointer"
                      style={{
                        marginBottom: 16,
                        height: 32,
                        borderColor:
                          selectedValue === "management" ? "#03B0F7" : "",
                        backgroundColor:
                          selectedValue === "management" ? "#EDF9FE" : "",
                      }}
                      onClick={() => setSelectedValue("management")}
                    >
                      <Radio
                        className={
                          selectedValue === "management" ? "selected-radio" : ""
                        }
                        style={{
                          color:
                            selectedValue === "management" ? "#03B0F7" : "",
                        }}
                        checked={selectedValue === "management"}
                        value="management"
                      >
                        Management
                      </Radio>
                    </div>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Form>
        </Modal>
      );
    }
  };
  return modalBody();
};

export default OrdersAlertsModal;

const Container = styled.div`
  .ant-modal-wrap {
    z-index: 1000 !important;
  }
  .ant-modal-mask {
    z-index: 1000 !important;
  }
`;
